import type { Price } from '@/scopes/prices/domain/prices/types'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPricesViewedClickEvent } from '@/scopes/prices/domain/analytics'
import { Dot, radius, space, Table, tok, tokPx } from '@sunwebgroup/ui-components'
import { Icon, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { css, styled } from 'styled-components'

const StyledPriceCell = styled(Table.Cell)<{
  $hasPrice: boolean
  $selected: boolean
  $isBestValue: boolean
}>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  border: none;
  border-radius: ${radius('primitives_corner_radius_m')} !important;
  margin-bottom: ${space('primitives_space_6')};
  background: ${tok('semantics_views_tinted_background_color')};
  min-height: 66px;
  width: auto;

  &:before {
    width: unset;
  }

  ${Dot} {
    position: absolute;
    top: ${space('primitives_space_4')};
    right: ${space('primitives_space_4')};
  }

  ${({ $hasPrice }) =>
    $hasPrice
      ? css`
          cursor: pointer;
        `
      : css`
        pointer-events: none;
        background: none;
      `}

  ${({ $selected, $hasPrice, $isBestValue }) =>
    !$selected && $hasPrice && !$isBestValue
    && css`
      &:hover {
        background: ${tok('primitives_color_neutral_50')};

        &:after {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: ${radius('primitives_corner_radius_m')} ;
          border: ${tokPx('semantics_dividers_tickness')} solid ${tok('primitives_color_neutral_200')};
          left: 0;
          top: 0;
        }
      }
    `}

  ${({ $isBestValue }) =>
    $isBestValue
    && css`
      background: ${tok('primitives_color_green_50')};
      color: ${tok('primitives_color_green_600')};

      &:hover {
        border: none;

        &:after {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: ${radius('primitives_corner_radius_m')};
          border: ${tokPx('semantics_dividers_tickness')} solid ${tok('primitives_color_green_200')};
          left: 0;
          top: 0;
        }
      }
    `}

  ${({ $selected, $isBestValue }) =>
    $selected
    && css`
      background: ${tok('primitives_color_selected_500')};
      color: ${tok('primitives_color_neutral_0')};
      ${$isBestValue
      && css`
        border: ${tokPx('semantics_dividers_tickness')} solid ${tok('primitives_color_green_600')};
      `}
    `}
`

const BulletIcon = styled(Icon)<{ $toBack?: boolean }>`
  position: absolute;
  right: ${space('primitives_space_4')};
  top: ${space('primitives_space_4')};
  z-index: 100;
  ${({ $toBack }) =>
    $toBack
    && css`
      right: 15px;
      z-index: 99;
    `}
`
export const PriceCellByWeeks = React.memo(
  ({
    price,
    onClick,
    isSelected,
  }: {
    price: Price | undefined
    onClick: (priceId: string) => void
    isSelected: boolean
  }) => {
    const intl = useIntl()

    const { sendEvent } = useAnalytics()

    const id = price?.id ?? ''
    const duration = price?.duration?.toString() ?? ''

    const onClickHandler = React.useCallback(() => {
      if (price) {
        sendEvent(
          createPricesViewedClickEvent({
            price: price.minPricePerPerson,
            isHighlighted: price.isBestValue,
          }),
        )
        onClick(id)
      }
    }, [onClick, price, id])

    const priceText = price?.minPricePerPerson ? intl.formatTruncatedCurrency(Math.ceil(price.minPricePerPerson)) : '-'
    const hasPrice = Boolean(price?.minPricePerPerson)
    return (
      <StyledPriceCell
        $hasPrice={hasPrice}
        onClick={onClickHandler}
        $selected={isSelected && hasPrice}
        $isBestValue={price?.isBestValue ?? false}
        data-qa-price-value={priceText}
      >
        {price?.bulletIcon && <BulletIcon size={16} glyph={price.bulletIcon} $toBack={price?.isBestValue} />}
        {price?.isBestValue && <BulletIcon glyph="ArrowDownInCircleMulticolor" size={16} />}
        <Typography $variant="body-l-bold-flat">{priceText}</Typography>
        <Typography $variant="body-xs-regular-flat">{duration ? `${duration} days` : ''}</Typography>
      </StyledPriceCell>
    )
  },
)

PriceCellByWeeks.displayName = 'PriceCellByWeeks'
